import { Project, Node, Edge } from '../types';
import { isEqual } from 'lodash-es'; // Using lodash for deep comparison

/**
 * Checks if there are unsaved changes by comparing the current flow state
 * with the state stored in the project context.
 *
 * @param project The current project object from ProjectContext (contains saved state).
 * @param currentNodes The current nodes array from Zustand store.
 * @param currentEdges The current edges array from Zustand store.
 * @returns True if there are unsaved changes, false otherwise.
 */
export function hasUnsavedChanges(
  project: Project | null,
  currentNodes: Node[],
  currentEdges: Edge[]
): boolean {
  // If there's no project loaded (e.g., initial state), consider it "unsaved" only if there are nodes/edges
  if (!project || project.id === 'unsaved-project') {
    return currentNodes.length > 0 || currentEdges.length > 0;
  }

  // Compare current nodes/edges with the saved ones in the project context
  // Using lodash isEqual for deep comparison, ignoring positional changes if needed,
  // but for now, a direct deep comparison is standard.
  // Note: Ensure node/edge objects don't contain volatile data like selection status
  // if you only want to compare structural/data changes. For simplicity, we compare everything.

  const savedNodes = project.nodes || [];
  const savedEdges = project.edges || [];

  // Basic length check first for performance
  if (currentNodes.length !== savedNodes.length || currentEdges.length !== savedEdges.length) {
    return true;
  }

  // Deep comparison if lengths match
  // Warning: This can be computationally expensive for very large graphs.
  // Consider optimizing if performance becomes an issue (e.g., comparing checksums, specific properties).
  const nodesChanged = !isEqual(currentNodes, savedNodes);
  const edgesChanged = !isEqual(currentEdges, savedEdges);

  return nodesChanged || edgesChanged;
}
